import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ThunkAction } from "redux-thunk";
import { Action } from "redux";
import { RootState } from "./rootReducer";
import { batch } from 'react-redux';
import { calRangeType, setCalRangeUsed, setCalRangeValue, syncAllViewDates, viewType, setIsAutoNavigating, setGradientLoading } from "./View_Slice";
import { doQuery } from "./Query_Slice";
import { buildQueryViewParams } from "../components/utils/queryHelper";
import CONSTANTS from "../constants";
import { addOneFacetSelected, removeOneFacetSelected, setFacetSelected } from "./Facets_Slice";
import { setLoading } from "./Loading_Slice";
import { setSummaryLoading } from "./Summary_Slice";
import { fetchLatestDate } from "./api/latestDateApi";
import { convertDateToGranularity } from "../components/utils/dateGranularity";
import { addToQueue } from "./Toasts_Slice";
import { createToastProperties, toastType } from "../components/toast/Toast";

/**
 * FacetNavigation_Slice
 *
 * Manages facet filter navigation with proper orchestration:
 * - Combines facet selection with date navigation in single thunks
 * - Tracks pending operations per-filter (no global blocking)
 * - Supports cancellation for rapid toggling
 * - Provides error handling with toast notifications
 */

// Type for a single stack entry
export interface DateStackEntry {
    filter: string;                    // "value@facetName" format
    savedCalRangeValue: string;        // The date value at time of filter application
    savedCalRangeUsed: calRangeType;   // The view type at time of filter application
    timestamp: number;                 // For debugging
}

// Type for latest date info display
export interface LatestDateInfo {
    date: string;              // Full date "2024-03-15"
    filters: string[];         // Applied filters at time of navigation
    timestamp: number;         // When navigation occurred
}

// Type for the original date before any filters were applied
export interface OriginalDateInfo {
    calRangeValue: string;
    calRangeUsed: calRangeType;
    timestamp: number;
}

// State type
interface FacetNavigationState {
    dateStack: DateStackEntry[];
    pendingOperations: { [filterId: string]: boolean };  // Track in-flight operations per filter
    latestDateInfo: LatestDateInfo | null;  // Track the latest date we navigated to for display
    originalDate: OriginalDateInfo | null;  // The date before the FIRST filter was applied
    // Store abort controllers outside Redux state (not serializable)
}

const initialState: FacetNavigationState = {
    dateStack: [],
    pendingOperations: {},
    latestDateInfo: null,
    originalDate: null,
};

// Store AbortControllers outside Redux (they're not serializable)
const abortControllers: Map<string, AbortController> = new Map();

const facetNavigationSlice = createSlice({
    name: 'FacetNavigation',
    initialState,
    reducers: {
        // Push a new entry onto the stack
        pushDate(state, action: PayloadAction<DateStackEntry>) {
            state.dateStack.push(action.payload);
        },

        // Remove an entry by filter string
        popDate(state, action: PayloadAction<string>) {
            const index = state.dateStack.findIndex(entry => entry.filter === action.payload);
            if (index !== -1) {
                state.dateStack.splice(index, 1);
            }
        },

        // Clear entire stack
        clearStack(state) {
            state.dateStack = [];
        },

        // Mark operation as started
        startOperation(state, action: PayloadAction<string>) {
            state.pendingOperations[action.payload] = true;
        },

        // Mark operation as completed
        completeOperation(state, action: PayloadAction<string>) {
            delete state.pendingOperations[action.payload];
        },

        // Mark operation as cancelled (different from complete for tracking)
        cancelOperation(state, action: PayloadAction<string>) {
            delete state.pendingOperations[action.payload];
        },

        // Set the latest date info for display in the info bar
        setLatestDateInfo(state, action: PayloadAction<LatestDateInfo>) {
            state.latestDateInfo = action.payload;
        },

        // Clear the latest date info (when filters removed or search activated)
        clearLatestDateInfo(state) {
            state.latestDateInfo = null;
        },

        // Set the original date (before the first filter was applied)
        setOriginalDate(state, action: PayloadAction<OriginalDateInfo>) {
            state.originalDate = action.payload;
        },

        // Clear the original date (when all filters are removed)
        clearOriginalDate(state) {
            state.originalDate = null;
        },

        // Set latest date info from URL restoration (no API call needed)
        // Used when filters AND date are restored from URL on page reload
        setLatestDateInfoFromUrl(state, action: PayloadAction<{
            date: string;
            filters: string[];
        }>) {
            state.latestDateInfo = {
                date: action.payload.date,
                filters: action.payload.filters,
                timestamp: Date.now()
            };
        },
    },
});

export const {
    pushDate,
    popDate,
    clearStack,
    startOperation,
    completeOperation,
    cancelOperation,
    setLatestDateInfo,
    clearLatestDateInfo,
    setOriginalDate,
    clearOriginalDate,
    setLatestDateInfoFromUrl
} = facetNavigationSlice.actions;

// Type for async thunks that return a Promise
type AppThunkAsync<ReturnType = void> = ThunkAction<
    Promise<ReturnType>,
    RootState,
    unknown,
    Action<string>
>;

/**
 * Combined thunk: Apply a facet filter with automatic date navigation
 *
 * Flow:
 * 1. Cancel any pending operation for the same filter (rapid re-click)
 * 2. Save current date to stack
 * 3. Add facet to selected (triggers Query.tsx)
 * 4. Fetch latest date with AbortController support
 * 5. Navigate to that date on success, rollback on failure
 */
export const applyFacetWithNavigation = (
    filter: string
): AppThunkAsync => async (dispatch, getState) => {
    const state = getState();

    // 1. Check if there's already a pending operation for this filter
    if (state.FacetNavigation.pendingOperations[filter]) {
        // Abort the existing operation
        const existingController = abortControllers.get(filter);
        if (existingController) {
            existingController.abort();
        }
        dispatch(cancelOperation(filter));
    }

    // 2. Save current date BEFORE adding facet
    const currentDate: DateStackEntry = {
        filter,
        savedCalRangeValue: state.View.calRangeValue,
        savedCalRangeUsed: state.View.calRangeUsed ?? calRangeType.month,
        timestamp: Date.now()
    };

    // Batch all synchronous pre-fetch dispatches into a single render
    batch(() => {
        dispatch(pushDate(currentDate));

        // 2b. If this is the FIRST filter (stack was empty), save the original date separately
        if (state.FacetNavigation.dateStack.length === 0) {
            dispatch(setOriginalDate({
                calRangeValue: state.View.calRangeValue,
                calRangeUsed: state.View.calRangeUsed ?? calRangeType.month,
                timestamp: Date.now()
            }));
        }

        // 3. Set auto-navigating flag BEFORE changing facetSelected
        // This prevents Query.tsx from firing intermediate API calls
        dispatch(setIsAutoNavigating(true));

        // 4. Add facet to selected (Query.tsx will see isAutoNavigating=true)
        dispatch(addOneFacetSelected(filter));

        // Show loading indicators immediately — same render as "filter applied"
        dispatch(setLoading(true));
        dispatch(setSummaryLoading(true));
        dispatch(setGradientLoading(true));
    });

    // 5. Check if search is active - skip date navigation but keep stack entry
    const hasSearch = state.Search.freeTextSearch?.trim();
    if (hasSearch) {
        // Clear flag since we're not doing date navigation
        setTimeout(() => dispatch(setIsAutoNavigating(false)), 0);
        return;
    }

    // 5. Mark operation as pending and create AbortController
    dispatch(startOperation(filter));
    const abortController = new AbortController();
    abortControllers.set(filter, abortController);

    try {
        // 6. Build the updated facet list (explicitly include the new filter)
        const updatedFacets = [...state.Facets.facetSelected, filter];

        // 7. Fetch latest date
        const result = await fetchLatestDate(updatedFacets, undefined, abortController.signal);

        // 8. Check if operation was cancelled during fetch
        const currentState = getState();
        if (!currentState.FacetNavigation.pendingOperations[filter]) {
            return;
        }

        if (result.success && result.formatted_date) {
            const formattedDate = result.formatted_date;

            // 9. Convert to appropriate granularity for current view
            const targetDate = convertDateToGranularity(
                formattedDate,
                state.View.calRangeUsed
            );

            // Batch synchronous post-fetch dispatches into a single render
            batch(() => {
                // 10. Sync all view dates using the full date from API
                // This ensures week/month/year views all stay in the same time period
                dispatch(syncAllViewDates(formattedDate));

                // 11. Navigate calendar to the converted date
                // (isAutoNavigating is already true from before facet change)
                dispatch(setCalRangeValue(targetDate));

                // 12. Store the latest date info for display in the info bar
                dispatch(setLatestDateInfo({
                    date: formattedDate,
                    filters: updatedFacets,
                    timestamp: Date.now()
                }));
            });

            // 11b. Dispatch query for calendar views (week view needs actual docs, not just heatmap)
            // Similar to how DateNav.tsx dispatches queries after navigation
            if (state.View.viewType === viewType.Calendar) {
                const viewparams = buildQueryViewParams(
                    state.View.viewType,
                    0,  // cursorStart
                    state.View.calRangeUsed === calRangeType.week
                        ? CONSTANTS.UI.CALENDAR_ITEM_PER_WEEK
                        : state.View.pageRangeUsed,
                    state.View.listSortedFeld,
                    state.View.listSortedOrder,
                    state.View.calRangeUsed,
                    targetDate,
                    updatedFacets,
                    false
                );

                // Get current request from Query state and dispatch
                const queryState = getState().Query;
                (dispatch as any)(doQuery(queryState.request + "&" + viewparams));
            }
        } else if (!result.success && result.error) {
            // 11. API returned an error - show toast and rollback
            // Only show toast for real errors, not "cancelled" or "no data"
            if (result.error !== 'Request cancelled') {
                const toastProps = createToastProperties(
                    toastType.warning,
                    'Filter Applied',
                    'Could not navigate to latest date. Showing current view.'
                );
                dispatch(addToQueue(toastProps));
            }

            // Rollback stack entry since we couldn't navigate
            dispatch(popDate(filter));
        } else {
            // 12. No data found - keep filter applied, stay on current date
            // Don't pop the stack - the filter was applied, just no navigation happened
        }
    } catch (error) {
        // 13. Unexpected error - rollback and notify
        // Check if it was an abort (don't show toast for aborts)
        if (error instanceof Error && error.name === 'AbortError') {
            // Request was aborted - no action needed
        } else {
            const toastProps = createToastProperties(
                toastType.error,
                'Navigation Error',
                'Could not fetch latest date. Please try again.'
            );
            dispatch(addToQueue(toastProps));
        }

        // Rollback stack entry
        dispatch(popDate(filter));
    } finally {
        // 14. Clean up
        dispatch(completeOperation(filter));
        abortControllers.delete(filter);
        // Delay clearing to allow React to flush effects with isAutoNavigating=true
        setTimeout(() => {
            dispatch(setIsAutoNavigating(false));
        }, 0);
    }
};

/**
 * Combined thunk: Remove a facet filter with date restoration
 *
 * Flow:
 * 1. Cancel any pending operation for this filter
 * 2. Remove facet from selected (triggers Query.tsx)
 * 3. Clear latest date info if all filters removed
 * 4. Skip date restore if search is active
 * 5. Restore saved date if found in stack
 * 6. Clean up stack entry
 */
export const removeFacetWithRestore = (
    filter: string
): AppThunkAsync => async (dispatch, getState) => {
    const state = getState();

    // 1. Cancel any pending operation for this filter
    if (state.FacetNavigation.pendingOperations[filter]) {
        const existingController = abortControllers.get(filter);
        if (existingController) {
            existingController.abort();
        }
        dispatch(cancelOperation(filter));
        abortControllers.delete(filter);
    }

    // 2-3. Batch auto-navigating flag and facet removal
    batch(() => {
        dispatch(setIsAutoNavigating(true));
        dispatch(removeOneFacetSelected(filter));

        // Show loading indicators immediately — same render as "filter removed"
        dispatch(setLoading(true));
        dispatch(setSummaryLoading(true));
        dispatch(setGradientLoading(true));
    });

    // 4. Calculate remaining filters AFTER removal
    const remainingFilters = state.Facets.facetSelected.filter(f => f !== filter);

    // 5. Skip date navigation if search is active
    if (state.Search.freeTextSearch?.trim()) {
        dispatch(popDate(filter));  // Still clean up stack
        // Clear flag since we're not doing date navigation
        setTimeout(() => dispatch(setIsAutoNavigating(false)), 0);
        return;
    }

    // 5. If there are remaining filters, fetch latest date for them instead of restoring old date
    if (remainingFilters.length > 0) {
        // Mark operation as pending and create AbortController
        dispatch(startOperation(filter + '_remove'));
        const abortController = new AbortController();
        abortControllers.set(filter + '_remove', abortController);

        try {
            // Fetch latest date for REMAINING filters
            const result = await fetchLatestDate(remainingFilters, undefined, abortController.signal);

            if (result.success && result.formatted_date) {
                const formattedDate = result.formatted_date;

                // Convert to appropriate granularity for current view
                const targetDate = convertDateToGranularity(
                    formattedDate,
                    state.View.calRangeUsed
                );

                // Batch synchronous post-fetch dispatches
                batch(() => {
                    // Sync all view dates using the full date from API
                    dispatch(syncAllViewDates(formattedDate));

                    // Navigate calendar to the converted date
                    // (isAutoNavigating is already true from before facet change)
                    dispatch(setCalRangeValue(targetDate));

                    // Update the latest date info for remaining filters
                    dispatch(setLatestDateInfo({
                        date: formattedDate,
                        filters: remainingFilters,
                        timestamp: Date.now()
                    }));
                });

                // Dispatch query for calendar views (week view needs actual docs)
                if (state.View.viewType === viewType.Calendar) {
                    const viewparams = buildQueryViewParams(
                        state.View.viewType,
                        0,  // cursorStart
                        state.View.calRangeUsed === calRangeType.week
                            ? CONSTANTS.UI.CALENDAR_ITEM_PER_WEEK
                            : state.View.pageRangeUsed,
                        state.View.listSortedFeld,
                        state.View.listSortedOrder,
                        state.View.calRangeUsed,
                        targetDate,
                        remainingFilters,
                        false
                    );

                    const queryState = getState().Query;
                    (dispatch as any)(doQuery(queryState.request + "&" + viewparams));
                }
            } else {
                // Fall back to restoring saved date
                const entry = state.FacetNavigation.dateStack.find(e => e.filter === filter);
                if (entry) {
                    dispatch(setCalRangeValue(entry.savedCalRangeValue));
                }
            }
        } catch (error) {
            // Fall back to restoring saved date
            const entry = state.FacetNavigation.dateStack.find(e => e.filter === filter);
            if (entry) {
                dispatch(setCalRangeValue(entry.savedCalRangeValue));
            }
        } finally {
            dispatch(completeOperation(filter + '_remove'));
            abortControllers.delete(filter + '_remove');
            // Delay clearing to allow React to flush effects with isAutoNavigating=true
            setTimeout(() => {
                dispatch(setIsAutoNavigating(false));
            }, 0);
        }
    } else {
        // 6. No remaining filters - clear latest date info and restore ORIGINAL date
        // Use the stored originalDate (saved when the FIRST filter was applied)
        const originalDate = state.FacetNavigation.originalDate;

        if (originalDate) {
            // Construct a full date from the original date value
            let fullOriginalDate = originalDate.calRangeValue;
            if (originalDate.calRangeUsed === calRangeType.month) {
                fullOriginalDate = originalDate.calRangeValue + '-01'; // e.g., "2026-01-01"
            } else if (originalDate.calRangeUsed === calRangeType.year) {
                fullOriginalDate = originalDate.calRangeValue + '-01-01'; // e.g., "2026-01-01"
            }
            // For week, it's already in yyyy-MM-dd format

            // Batch all synchronous restore dispatches
            batch(() => {
                dispatch(clearLatestDateInfo());

                // Restore view type if different
                if (originalDate.calRangeUsed !== state.View.calRangeUsed) {
                    dispatch(setCalRangeUsed(originalDate.calRangeUsed));
                }

                // Restore date value
                // (isAutoNavigating is already true from before facet change)
                dispatch(setCalRangeValue(originalDate.calRangeValue));

                // IMPORTANT: Also sync all view dates to the original date
                // This ensures switching between month/week/year views won't jump back to filter dates
                dispatch(syncAllViewDates(fullOriginalDate));

                // Clear the original date since we've restored it
                dispatch(clearOriginalDate());
            });

            // Dispatch query for calendar views (week view needs actual docs)
            if (state.View.viewType === viewType.Calendar) {
                const viewparams = buildQueryViewParams(
                    state.View.viewType,
                    0,  // cursorStart
                    originalDate.calRangeUsed === calRangeType.week
                        ? CONSTANTS.UI.CALENDAR_ITEM_PER_WEEK
                        : state.View.pageRangeUsed,
                    state.View.listSortedFeld,
                    state.View.listSortedOrder,
                    originalDate.calRangeUsed,
                    originalDate.calRangeValue,
                    [],  // No remaining filters
                    false
                );

                const queryState = getState().Query;
                (dispatch as any)(doQuery(queryState.request + "&" + viewparams));
            }

            // Delay clearing to allow React to flush effects with isAutoNavigating=true
            setTimeout(() => {
                dispatch(setIsAutoNavigating(false));
            }, 0);
        } else {
            dispatch(clearLatestDateInfo());
            // No originalDate stored - just clear the flag
            setTimeout(() => {
                dispatch(setIsAutoNavigating(false));
            }, 0);
        }
    }

    // 7. Clean up stack entry for the removed filter
    dispatch(popDate(filter));
};

/**
 * Combined thunk: Clear all facet filters with calendar restoration
 *
 * Used when exiting search mode (X button) to properly clean up both
 * facet selections AND the FacetNavigation state.
 *
 * Flow:
 * 1. Cancel ALL pending operations
 * 2. Clear all facets
 * 3. Clear navigation state (stack, latestDateInfo)
 * 4. Restore original date if available
 * 5. Sync all view dates to the original position
 */
export const clearAllFacetsWithRestore = (): AppThunkAsync => async (dispatch, getState) => {
    const state = getState();

    // 1. Cancel ALL pending operations
    for (const filter of Object.keys(state.FacetNavigation.pendingOperations)) {
        const controller = abortControllers.get(filter);
        if (controller) {
            controller.abort();
        }
        abortControllers.delete(filter);
    }

    // 4. Restore original date if available
    const originalDate = state.FacetNavigation.originalDate;
    if (originalDate) {
        // Construct a full date from the original date value
        let fullDate = originalDate.calRangeValue;
        if (originalDate.calRangeUsed === calRangeType.month) {
            fullDate = originalDate.calRangeValue + '-01'; // e.g., "2026-01" -> "2026-01-01"
        } else if (originalDate.calRangeUsed === calRangeType.year) {
            fullDate = originalDate.calRangeValue + '-01-01'; // e.g., "2026" -> "2026-01-01"
        }
        // For week, it's already in yyyy-MM-dd format

        // Batch all synchronous dispatches: clear facets, navigation state, and restore date
        batch(() => {
            // 2. Clear all facets
            dispatch(setFacetSelected([]));

            // 3. Clear navigation state
            dispatch(clearStack());
            dispatch(clearLatestDateInfo());

            // Set auto-navigating flag BEFORE date change to prevent duplicate history entries
            dispatch(setIsAutoNavigating(true));

            // Show loading indicators immediately — same render as "all filters cleared"
            dispatch(setLoading(true));
            dispatch(setSummaryLoading(true));
            dispatch(setGradientLoading(true));

            // Restore date value
            dispatch(setCalRangeValue(originalDate.calRangeValue));

            // Restore view type if different
            if (originalDate.calRangeUsed !== state.View.calRangeUsed) {
                dispatch(setCalRangeUsed(originalDate.calRangeUsed));
            }

            // 5. Sync all view dates to the original position
            dispatch(syncAllViewDates(fullDate));

            // Clear the original date since we've restored it
            dispatch(clearOriginalDate());
        });

        // Delay clearing to allow React to flush effects with isAutoNavigating=true
        setTimeout(() => {
            dispatch(setIsAutoNavigating(false));
        }, 0);
    } else {
        // No original date - just clear facets and navigation state
        batch(() => {
            dispatch(setFacetSelected([]));
            dispatch(clearStack());
            dispatch(clearLatestDateInfo());
        });
    }
};

export default facetNavigationSlice.reducer;
